import { API_ENDPOINTS } from '../api/apiEndpoints';
import axios from '../api/axiosInstance';
import { getRefreshToken, setRefreshToken, removeRefreshToken } from '../utils/cookieUtils';

/**
 * Checks if a JWT token is expired
 */
export const isTokenExpired = (token) => {
    if (!token || token.split('.').length !== 3) {
      return true;
    }
  
    try {
      const payloadBase64 = token.split('.')[1];
      const payloadJson = atob(payloadBase64);
      const payload = JSON.parse(payloadJson);
  
      return payload.exp * 1000 < Date.now(); // JWT 'exp' is in seconds; Date.now() is in ms
    } catch (e) {
      console.error('Token decode failed:', e);
      return true; // consider expired if decoding fails
    }
  };
  
  /**
   * Handles the full auth flow:
   * - Checks access token
   * - Refreshes token if needed
   * - Logs out if refresh token is expired or refresh fails
   */
  export const handleTokenAuthFlow = async () => {
    const accessToken = localStorage.getItem('access_token');
    const refreshToken = getRefreshToken(); // Now using cookie
  
    // Case 1: No tokens stored (user not logged in)
    if (!accessToken || !refreshToken) {
      return false;
    }
  
    // Case 2: Access token is valid
    if (!isTokenExpired(accessToken)) {
      return true;
    }
  
    // Case 3: Try refreshing if refresh token is still valid
    if (!isTokenExpired(refreshToken)) {
      try {
        // Use fetch for token refresh to avoid circular dependency with axios interceptor
        const response = await fetch(API_ENDPOINTS.refreshToken, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
  
        if (!response.ok) {
          throw new Error('Refresh Failed');
        }
  
        const data = await response.json();
        // console.log('Token refresh response:', data);
  
        if (data.access_token) {
          localStorage.setItem('access_token', data.access_token);
          // console.log('Access token set successfully');
          return true;
        } else {
          console.warn('Access token not found in response');
        }
      } catch (error) {
        console.error('Error during refresh:', error);
        // fall through to logout
      }
    }
  
    // Case 4: Both tokens are invalid → logout
    try {
      // Use fetch for logout to avoid circular dependency with axios interceptor
      await fetch(API_ENDPOINTS.logout, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refresh_token: refreshToken }),
      });
    } catch (e) {
      console.warn('Logout request failed (may be already logged out):', e);
    }
  
    localStorage.removeItem('access_token');
    removeRefreshToken(); // Now using cookie utility
    return false;
  };
  
  /**
   * Login using employee ID and password.
   * Saves tokens appropriately on success.
   */
  export const login = async (username, password) => {
    const response = await axios.post(`${API_ENDPOINTS.login}`, {
      employee_id: username,
      password,
    });
  
    localStorage.setItem('access_token', response.data.access_token);
    setRefreshToken(response.data.refresh_token); // Now using secure cookie
  
    return response.data;
  };
  
  /**
   * Requests a new access token using the refresh token.
   * Returns new access_token or null on failure.
   */
  export const refreshToken = async () => {
    const refreshToken = getRefreshToken(); // Now using cookie
    if (!refreshToken) return null;
  
    // Use fetch for token refresh to avoid circular dependency with axios interceptor
    const response = await fetch(API_ENDPOINTS.refreshToken, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });
  
    if (response.ok) {
      const data = await response.json();
      localStorage.setItem('access_token', data.access_token);
      return data.access_token;
    } else {
      return null;
    }
  };
  
  /**
   * Logs out the user by removing tokens.
   * Note: does not notify backend. Use handleTokenAuthFlow() for full logout.
   */
  export const logout = async () => {
    const refreshToken = getRefreshToken(); // Now using cookie
  
    if (refreshToken) {
      try {
        // Use fetch for logout to avoid circular dependency with axios interceptor
        await fetch(API_ENDPOINTS.logout, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ refresh_token: refreshToken }),
        });
      } catch (error) {
        console.warn('Logout request failed:', error);
      }
    }
  
    localStorage.removeItem('access_token');
    removeRefreshToken(); // Now using cookie utility
  };

  export const firstLoginReset = async (employeeId, oldPassword, newPassword) => {
    const response = await axios.post(`${API_ENDPOINTS.firstLoginReset}`, {
      employee_id: employeeId,
      old_password: oldPassword,
      new_password: newPassword,
    });

    return response.data;
  }